import { v4 as uuid_v4 } from "uuid";
import * as CryptoJS from "crypto-js";

import AuthService from "../api/auth-service";
import ProductService from "../api/product-service";
import { toast } from "react-toastify";
import { RSA_PUBLIC_KEY } from "../consts/auth";
import { useDispatch } from "react-redux";
import { generateBaseUrl } from "../api/generateBaseUrl";

import secureLocalStorage from "react-secure-storage";
import { AUTH_API_URL, PRODUCT_API_URL } from "../api/urlMetaData";
import {
  setActiveRegion,
  setCountryList,
  setRegions,
} from "../store/features/globalSlice";
import { COUNTRIES } from "../consts/AppContants";

import {
  AUTH_LOCAL_VARIABLES,
  setAuthToken,
  setCsrfToken,
  setCurrentUser,
  setLoggedIn,
} from "../store/features/authSlice";

// Encryption Helpers
export const generateRandomUUID = () => uuid_v4();

export const getAesEncryptedData = (text, key) => {
  return CryptoJS.AES.encrypt(text, key).toString();
};

export const getRsaEncryptedData = (information, JSEncrypt) => {
  const encryptionEngine = new JSEncrypt();
  encryptionEngine.setPublicKey(RSA_PUBLIC_KEY);
  return encryptionEngine.encrypt(information);
};

export const uuid = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getCountryName = {
  AE: COUNTRIES.COUNTRY_NAME_UAE,
  IN: COUNTRIES.COUNTRY_NAME_INDIA,
  SA: COUNTRIES.COUNTRY_NAME_KSA,
  EG: COUNTRIES.COUNTRY_NAME_EGYPT,
};

const getRegionByCountryName = {
  [COUNTRIES.COUNTRY_NAME_UAE]: "UAE",
  [COUNTRIES.COUNTRY_NAME_KSA]: "KSA",
  [COUNTRIES.COUNTRY_NAME_EGYPT]: "EGYPT",
  [COUNTRIES.COUNTRY_NAME_INDIA]: "INDIA",
};

const useAuth = () => {
  const dispatch = useDispatch();

  const sendOTP = async (encryptedPayload = {}) => {
    try {
      const res = await AuthService.post(
        AUTH_API_URL.SEND_OTP_URL,
        encryptedPayload,
        {
          headers: {
            X_REQUEST_ID: uuid(),
            X_CLIENT_ID: "Cards-UI",
          },
        }
      );

      const { data, headers } = res;

      if (data?.status === "OK") {
        dispatch(setCsrfToken(headers[AUTH_LOCAL_VARIABLES.csrf]));
        toast("OTP Sent Successfully!", { type: "success" });
        return res;
      }
    } catch (error) {
      toast(error?.message, { type: "error" });
      toast(error?.response?.data.message, { type: "error" });
      return error;
    }
  };

  const verifyOTP = async (encryptedPayload) => {
    try {
      // get csrf token from store or localstorage
      const csrf = secureLocalStorage.getItem(AUTH_LOCAL_VARIABLES.csrf);

      const res = await AuthService.post(
        AUTH_API_URL.VERIFY_OTP_URL,
        encryptedPayload,
        {
          headers: {
            csrf,
          },
        }
      );

      if (!res) {
        return {
          multipleRegions: false,
          success: false,
          choosePassword: false,
        };
      }

      const { data, status, headers } = res;
      if (status === 200) {
        // get auth header and set auth token
        dispatch(setAuthToken(headers.authorization));
        dispatch(setLoggedIn(true));
        // dispatch(setCurrentUser(data));

        // check if there are more than one region,
        // else select single region as default active region

        // multiple region
        // set regions of the user
        dispatch(setRegions(data.body.regionalUserList));

        // single region
        if (!data.body.regionalUserList[1]) {
          dispatch(setActiveRegion(data.body.regionalUserList[0].region));
          dispatch(setCurrentUser(data.body.regionalUserList[0]));
          return {
            success: true,
            multipleRegions: false,
            choosePassword: data.body.choosePassword,
          };
        }

        dispatch(setCurrentUser(data.body.regionalUserList[0]));
        return {
          success: true,
          multipleRegions: true,
          choosePassword: data.body.choosePassword,
        };
      }
    } catch (error) {
      toast(error?.response?.data.message, { type: "error" });
      return false;
    }
  };

  const fetchClientDetails = async () => {
    try {
      // get csrf token from store or localstorage
      const csrf = secureLocalStorage.getItem(AUTH_LOCAL_VARIABLES.csrf);
      const currentUser = secureLocalStorage.getItem(AUTH_LOCAL_VARIABLES.user);

      const res = await ProductService.get(
        PRODUCT_API_URL.FETCH_CLIENT + "/" + currentUser.clientHashId,
        {
          headers: {
            Authorization: secureLocalStorage.getItem(
              AUTH_LOCAL_VARIABLES.auth_token
            ),
            csrf,
          },
        }
      );

      const { data, status } = res;

      console.log("fetching client details..", res);

      if (!data) {
        toast("You are not Authorized User!", { type: "error" });
      }

      if (status === 200) {
        const userHashId = currentUser.userHashId;
        const client = { ...currentUser, ...res.data, userHashId };

        // get client programs
        const hasPrograms = await getAllPrograms(client.clientHashId);

        if (hasPrograms) {
          dispatch(setCurrentUser(client));
          return true;
        }
      }
    } catch (error) {
      toast(error?.response?.data.message, { type: "error" });
      return false;
    }
  };

  const getAllPrograms = async (clientHashId) => {
    try {
      const res = await ProductService.get(
        PRODUCT_API_URL.GET_MOXEY_PROGRAM_ID + clientHashId,
        {
          headers: {
            HEADER_X_REQUEST_ID: uuid_v4(),
            csrf: secureLocalStorage.getItem(AUTH_LOCAL_VARIABLES.csrf),
            Authorization: secureLocalStorage.getItem(
              AUTH_LOCAL_VARIABLES.auth_token
            ),
          },
        }
      );

      console.log("program list", res);

      let countryList = {};
      const countryProgramMap = {};
      const regionMap = {};
      let programs = [];

      if (res?.status === 200) {
        res.data.forEach((country) => {
          const programId = country.programCode;
          const countryName = getCountryName[country.country];

          countryProgramMap[countryName] = country.programCode;
          regionMap[programId] = getRegionByCountryName[countryName];
        });

        // Preparing ProgramCode and Country Map list as programs
        Object.keys(countryProgramMap).map((key) => {
          programs = [
            ...programs,
            {
              programHashId: countryProgramMap[key],
              country: key,
            },
          ];
        });

        // add country and region mapping
        // set in sessions (to do)
        countryList = {
          countryProgramMap,
          regionMap,
          programs,
        };

        // set country list to store
        dispatch(setCountryList(countryList));
        return true;
      }
    } catch (error) {
      // common error handler to be used here for all the APIS
      return error;
    }
  };

  return {
    sendOTP,
    verifyOTP,
    fetchClientDetails,
    getAllPrograms,
  };
};

export default useAuth;
